import type { OverlayLayout } from '../types';

/** Dynamic viewport height unit — falls back to vh for unsupported browsers. */
export const FULL_VIEWPORT_HEIGHT = typeof CSS !== 'undefined' && CSS.supports?.('height', '100dvh') ? '100dvh' : '100vh';
const CALC_DVH_MINUS_100 = typeof CSS !== 'undefined' && CSS.supports?.('height', '100dvh') ? 'calc(100dvh - 100px)' : 'calc(100vh - 100px)';

/**
 * MUI Paper elevation-4 box-shadow. Used on the iframe element itself in the
 * host page so the embedded chat overlay matches the shadow rendered by the
 * inner MUI Paper on the public chat page (where the Paper sits in the same
 * document and its shadow is not clipped).
 */
export const MUI_ELEVATION_4_SHADOW =
  '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)';

/**
 * Style utility functions for dynamic CSS generation
 */

/**
 * Convert CSS property name from camelCase to kebab-case
 */
export function camelToKebab(str: string): string {
  return str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
}

/**
 * Generate CSS from style object
 */
export function generateCSS(styles: Record<string, string | number>, selector?: string): string {
  const cssRules = Object.entries(styles)
    .map(([property, value]) => {
      const cssProperty = camelToKebab(property);
      const cssValue = typeof value === 'number' ? `${value}px` : value;
      return `  ${cssProperty}: ${cssValue};`;
    })
    .join('\n');

  if (selector) {
    return `${selector} {\n${cssRules}\n}`;
  }
  
  return cssRules;
}

/**
 * Get position styles based on layout configuration
 */
export function getPositionStyles(layout: OverlayLayout): Partial<CSSStyleDeclaration> {
  const { position, margin } = layout;
  const offset = margin ?? 20;

  const styles: Partial<CSSStyleDeclaration> = {
    position: 'fixed',
    zIndex: layout.zIndex?.toString() || '9999'
  };

  switch (position) {
    case 'bottom-right':
      styles.bottom = `${offset}px`;
      styles.right = `${offset}px`;
      break;
    case 'bottom-left':
      styles.bottom = `${offset}px`;
      styles.left = `${offset}px`;
      break;
    case 'top-right':
      styles.top = `${offset}px`;
      styles.right = `${offset}px`;
      break;
    case 'top-left':
      styles.top = `${offset}px`;
      styles.left = `${offset}px`;
      break;
    case 'center':
      styles.top = '50%';
      styles.left = '50%';
      styles.transform = 'translate(-50%, -50%)';
      break;
    default:
      // Default to bottom-right
      styles.bottom = `${offset}px`;
      styles.right = `${offset}px`;
  }

  return styles;
}

/**
 * Get position styles based on layout configuration
 */
export function getLauncherPositionStyles(layout: OverlayLayout): Partial<CSSStyleDeclaration> {
  const { position, launcherOffset } = layout;
  const offset = launcherOffset || { x: 20, y: 20 };
  
  const styles: Partial<CSSStyleDeclaration> = {
    position: 'fixed',
    zIndex: layout.zIndex?.toString() || '9999'
  };

  switch (position) {
    case 'bottom-right':
      styles.bottom = `${offset.y}px`;
      styles.right = `${offset.x}px`;
      break;
    case 'bottom-left':
      styles.bottom = `${offset.y}px`;
      styles.left = `${offset.x}px`;
      break;
    case 'top-right':
      styles.top = `${offset.y}px`;
      styles.right = `${offset.x}px`;
      break;
    case 'top-left':
      styles.top = `${offset.y}px`;
      styles.left = `${offset.x}px`;
      break;
    case 'center':
      styles.top = '50%';
      styles.left = '50%';
      styles.transform = 'translate(-50%, -50%)';
      break;
    default:
      // Default to bottom-right
      styles.bottom = `${offset.y}px`;
      styles.right = `${offset.x}px`;
  }

  return styles;
}

/**
 * Get launcher button styles based on layout configuration
 */
export function getLauncherStyles(layout: OverlayLayout): Partial<CSSStyleDeclaration> {
  const baseStyles = getLauncherPositionStyles(layout);
  
  return {
    ...baseStyles,
    width: `${layout.launcherSize || 60}px`,
    height: `${layout.launcherSize || 60}px`,
    borderRadius: layout.launcherStyle === 'square' ? '8px' : '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#ffffff',
    backgroundColor: '#439568', // Default green
    transition: layout.animations ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
    boxShadow: layout.shadow ? '0 4px 16px rgba(0, 0, 0, 0.15)' : 'none',
    outline: 'none',
    webkitUserSelect: 'none',
    touchAction: 'manipulation'
  };
}

/**
 * Get chat window styles based on layout configuration
 */
export function getChatWindowStyles(layout: OverlayLayout, isVisible: boolean = false): Partial<CSSStyleDeclaration> {
  const baseStyles = getPositionStyles(layout);
  
  // Adjust position for chat window (offset from launcher)
  const adjustedStyles = { ...baseStyles };
  const chatMargin = layout.margin || 80; // Space between launcher and chat window
  
  switch (layout.position) {
    case 'bottom-right':
      adjustedStyles.bottom = `${chatMargin}px`;
      break;
    case 'bottom-left':
      adjustedStyles.bottom = `${chatMargin}px`;
      break;
    case 'top-right':
      adjustedStyles.top = `${chatMargin}px`;
      break;
    case 'top-left':
      adjustedStyles.top = `${chatMargin}px`;
      break;
    case 'center':
      // Center position doesn't need adjustment
      break;
  }

  return {
    ...adjustedStyles,
    width: `${layout.width || 400}px`,
    height: `${layout.height || 600}px`,
    maxWidth: '100vw',
    maxHeight: FULL_VIEWPORT_HEIGHT,
    border: 'none',
    borderRadius: `${layout.borderRadius || 16}px`,
    boxShadow: layout.shadow ? MUI_ELEVATION_4_SHADOW : 'none',
    backgroundColor: 'transparent',
    display: isVisible ? 'block' : 'none',
    opacity: isVisible ? '1' : '0',
    transform: isVisible ? 'scale(1)' : 'scale(0.95)',
    transition: layout.animations ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
    pointerEvents: isVisible ? 'auto' : 'none'
  };
}

/**
 * Get responsive styles for mobile devices
 */
export function getMobileStyles(layout: OverlayLayout): Partial<CSSStyleDeclaration> {
  if (layout.mobileFullScreen) {
    return {
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      width: '100vw',
      height: FULL_VIEWPORT_HEIGHT,
      borderRadius: '0',
      maxWidth: '100vw',
      maxHeight: FULL_VIEWPORT_HEIGHT
    };
  }

  return {
    width: 'calc(100vw - 20px)',
    height: CALC_DVH_MINUS_100,
    maxWidth: 'calc(100vw - 20px)',
    maxHeight: CALC_DVH_MINUS_100,
    left: '10px',
    right: '10px'
  };
}

/**
 * Generate CSS animation keyframes
 */
export function generateAnimationCSS(): string {
  return `
@keyframes kaigentic-bounce {
  0%, 20%, 53%, 80%, 100% {
    transform: scale(1);
  }
  40%, 43% {
    transform: scale(1.1);
  }
}

@keyframes kaigentic-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(37, 99, 235, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
  }
}

@keyframes kaigentic-slide-in {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes kaigentic-slide-out {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
}

.kaigentic-bounce {
  animation: kaigentic-bounce 1s ease-in-out;
}

.kaigentic-pulse {
  animation: kaigentic-pulse 2s infinite;
}
`;
}

/**
 * Inject CSS into document head
 */
export function injectCSS(css: string, id?: string): HTMLStyleElement {
  const style = document.createElement('style');
  style.textContent = css;
  
  if (id) {
    style.id = id;
    // Remove existing style with same ID
    const existing = document.getElementById(id);
    if (existing) {
      existing.remove();
    }
  }
  
  document.head.appendChild(style);
  return style;
}

/**
 * Create CSS class name with prefix
 */
export function createClassName(name: string, prefix: string = 'kaigentic'): string {
  return `${prefix}-${name}`;
}

/**
 * Get theme-based colors
 */
export function getThemeColors(theme: 'light' | 'dark' | 'auto' = 'auto'): Record<string, string> {
  const isDark = theme === 'dark' || 
    (theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  if (isDark) {
    return {
      primary: '#03a96f',
      secondary: '#1e40af',
      background: '#080f25',
      surface: '#212c4d',
      text: '#f8fafc',
      textSecondary: '#94a3b8'
    };
  }

  return {
    primary: '#03a96f',
    secondary: '#1e40af',
    background: '#f9fafc',
    surface: '#ffffff',
    text: '#0f172a',
    textSecondary: '#475569'
  };
}

/**
 * Apply hover effects to element
 */
export function applyHoverEffect(element: HTMLElement, layout: OverlayLayout): void {
  if (!layout.animations) return;

  const originalTransform = element.style.transform || '';
  
  element.addEventListener('mouseenter', () => {
    element.style.transform = originalTransform + ' scale(1.1)';
    element.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.2)';
  });

  element.addEventListener('mouseleave', () => {
    element.style.transform = originalTransform;
    element.style.boxShadow = layout.shadow ? '0 4px 16px rgba(0, 0, 0, 0.15)' : 'none';
  });
}